import { FormSearchSelect } from "../../../../components/ui/Input/FormSearchSelect/FormSearchSelect";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Input } from "../../../../components/ui/common/input";
import { Label } from "../../../../components/ui/common/label";
import { IngredientRegisterViewModel } from "../types/ingredientRegisterView.types";
import IngredientImageUpload from "./IngredientImageUpload";
import RegisterFormProvider from "./RegisterFormProvider";

interface IngredientInfoCardProps {
  vm: IngredientRegisterViewModel;
}

/**
 * "INGREDIENT INFO" card — Ingredient Name (search-select, or an inline
 * "+ New" create form) + Supplier Name (search-select) + Ingredient Image.
 * Fully Tailwind: search-selects use `FormSearchSelect`
 * (src/components/ui/Input/FormSearchSelect/) bound to `vm.searchControl`,
 * image upload uses the local `IngredientImageUpload` — no Semantic UI
 * anywhere in this card.
 */
const IngredientInfoCard = ({ vm }: IngredientInfoCardProps) => {
  return (
    <Card className="tw-gap-3 tw-py-4">
      <CardHeader className="tw-px-4">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
          Ingredient Info
        </span>
      </CardHeader>
      <CardContent className="tw-flex tw-flex-col tw-gap-3 tw-px-4">
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
          <div>
            {vm.isCreatingNewIngredient ? (
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <Label>
                  Ingredient Name <span className="tw-ml-0.5 tw-text-destructive">*</span>
                </Label>
                <Input
                  value={vm.newIngredientName}
                  onChange={e => vm.onNewIngredientNameChange(e.target.value)}
                  placeholder="Type your ingredient name"
                />
                {vm.newIngredientError ? (
                  <p className="tw-text-xs tw-text-destructive">{vm.newIngredientError}</p>
                ) : null}
                <div className="tw-mt-1 tw-flex tw-gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      !vm.newIngredientName.trim() ||
                      Boolean(vm.newIngredientError) ||
                      vm.isCreatingIngredient
                    }
                    onClick={vm.onCreateIngredient}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={vm.onCancelCreateIngredient}
                  >
                    Back
                  </Button>
                </div>
              </div>
            ) : (
              <RegisterFormProvider control={vm.searchControl} errors={vm.searchErrors}>
                <FormSearchSelect
                  control={vm.searchControl}
                  name="ingredient_search"
                  label="Ingredient Name"
                  required
                  placeholder="Select or add ingredient"
                  options={vm.ingredientOptions as { id: string; title: string }[]}
                  onChange={vm.onSelectIngredient}
                />
              </RegisterFormProvider>
            )}
          </div>
          <RegisterFormProvider control={vm.searchControl} errors={vm.searchErrors}>
            <FormSearchSelect
              control={vm.searchControl}
              name="supplier_search"
              label="Supplier Name"
              placeholder="Select or add supplier"
              options={vm.supplierOptions as { id: string; title: string }[]}
              disabled={!vm.enableSupplierSearch}
              onChange={vm.onSelectSupplier}
            />
          </RegisterFormProvider>
        </div>

        <div>
          <Label>Ingredient Image</Label>
          <div className="tw-mt-1.5">
            <IngredientImageUpload
              imagePath={vm.imagePath}
              onUpload={vm.onImageUpload}
              onDelete={vm.onImageDelete}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default IngredientInfoCard;
